import { CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { AdditionOrderModalForm, SharedSpinner } from '@components';
import { useRelatedOrders, useView } from '@hooks';
import { L } from '@lib';
import { OrderStatus, ServiceOrderStatusTag, ServiceOrderStatusTagObjectKey } from '@models';
import { Order, Vehicle } from '@services';
import { useStore } from '@stores';
import { Button, Checkbox, Col, Row } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface IProps {
  order: Order | undefined;
  vehicle: Vehicle | undefined;
}

export const RelatedOrders: React.FC<IProps> = observer(
  ({ order, vehicle }) => {
    const { isMobile } = useView();
    const navigate = useNavigate();
    const { authenticationStore, orderStore } = useStore();
    const [openModal, setOpenModal] = useState(false);

    const currentUser = authenticationStore.currentUser;
    const userRole = currentUser?.role;

    const shouldHideCreateButton = userRole === 'Technician';

    const { relatedOrders: orders, relatedLoading: loading } = useRelatedOrders(order?.id);

    const isDisabled =
      order?.status === OrderStatus.Ready ||
      order?.status === OrderStatus.Paid ||
      order?.status === OrderStatus.Approval;

    return (
      <SharedSpinner loading={loading}>
        {!isDisabled && !shouldHideCreateButton && (
          <Row justify="end" style={{ marginBottom: 10 }}>
            <Col>
              <Button type="primary" onClick={() => setOpenModal(true)}>
                Create separate booking
              </Button>
            </Col>
          </Row>
        )}
        {orders.map((x) => {
          return (
            <div
              key={x.id}
              style={{
                background: '#fafafa',
                border: '1px solid #eee',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 10,
              }}
            >
              <Row gutter={[12, 12]} align="middle">
                <Col>
                  <Checkbox
                    style={{ margin: 0, marginTop: 2 }}
                    disabled={x.status !== OrderStatus.Completed}
                    onChange={(e) => orderStore.toggleRelatedCheck(x.id, e.target.checked)}
                  />
                </Col>

                <Col xs={24} sm={16}>
                  <Button
                    rel="noreferrer"
                    type="link"
                    target="_blank"
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: 0,
                      display: 'block',
                      background: 'transparent',
                      boxShadow: 'none',
                      height: 'auto',
                    }}
                    onClick={() => navigate(`/orders/${x.id}`)}
                  >
                    <div
                      style={{
                        fontWeight: 'bold',
                        fontSize: 14,
                        color: 'rgba(0, 0, 0, 0.85)',
                        marginBottom: 0,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 900, // Adjust as needed to fit your layout
                      }}
                    >
                      {x.title}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'rgba(0, 0, 0, 0.45)',
                        marginTop: 4,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <span>
                        <UserOutlined style={{ marginRight: 4, fontSize: 13 }} />
                        Technician:
                      </span>
                      <span>{(x.user && x.user.fullname) || L('Not Assigned')}</span>
                      {x.dropOffDate && (
                        <>
                          <span style={{ marginLeft: 8 }}> Drop-off date & time:</span>
                          <span>
                            <CalendarOutlined style={{ marginRight: 4, fontSize: 13 }} />
                            {dayjs(x.dropOffDate).format('YYYY-MM-DD HH:mm')}
                          </span>
                        </>
                      )}
                    </div>
                  </Button>
                </Col>
                <Col xs={24} sm={6}>
                  <div
                    style={{
                      padding: isMobile ? '0' : '4px 8px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 500,
                      textAlign: isMobile ? 'left' : 'center',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    {ServiceOrderStatusTag[x.status as ServiceOrderStatusTagObjectKey]}
                  </div>
                </Col>
              </Row>
            </div>
          );
        })}

        {openModal && vehicle && order && (
          <AdditionOrderModalForm
            order={order}
            vehicle={vehicle}
            open={openModal}
            setOpen={setOpenModal}
          />
        )}
      </SharedSpinner>
    );
  }
);
